import React from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, WalletIcon, GiftIcon, TrendingUpIcon, CreditCardIcon, AlertTriangleIcon, ShoppingBagIcon, RepeatIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { StatCard } from '../../components/ui/StatCard';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { formatVND, formatVNDCompact, formatRelativeTime } from '../../lib/formatters';
import { MOCK_DASHBOARD_STATS, MOCK_MEMBER_GROWTH, MOCK_DEPOSIT_TRENDS, MOCK_CREDIT_USAGE, MOCK_TIER_DISTRIBUTION, MOCK_CUSTOMERS, MOCK_AUDIT_LOGS } from '../../lib/mockData';
const TIER_COLORS = ['#666666', '#A0AEC0', '#C8A84E', '#EF4444'];
export function DashboardPage() {
  const stats = MOCK_DASHBOARD_STATS;
  const topCustomers = [...MOCK_CUSTOMERS].sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 5);
  const recentLogs = MOCK_AUDIT_LOGS.slice(0, 5);
  return <div data-id="element-757">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-758">
        <h1 className="text-2xl font-bold text-white mb-1" data-id="element-759">Tổng Quan</h1>
        <p className="text-sm text-[#666666] mb-6" data-id="element-760">
          Bảng điều khiển hệ thống khách hàng thân thiết
        </p>
      </motion.div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-761">
        <StatCard label="Tổng thành viên" value={stats.totalMembers.toLocaleString('vi-VN')} icon={<UsersIcon className="w-5 h-5" data-id="element-763" />} trend={{
        value: 8.5,
        isPositive: true
      }} data-id="element-762" />
        <StatCard label="Thành viên hoạt động (30 ngày)" value={stats.activeMembers30d.toLocaleString('vi-VN')} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-765" />} trend={{
        value: 5.2,
        isPositive: true
      }} data-id="element-764" />
        <StatCard label="Tổng nạp tín dụng" value={formatVNDCompact(stats.totalDeposited)} icon={<WalletIcon className="w-5 h-5" data-id="element-767" />} trend={{
        value: 12.3,
        isPositive: true
      }} data-id="element-766" />
        <StatCard label="Tổng thưởng phát hành" value={formatVNDCompact(stats.totalBonusIssued)} icon={<GiftIcon className="w-5 h-5" data-id="element-769" />} trend={{
        value: 12.3,
        isPositive: true
      }} data-id="element-768" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-770">
        <StatCard label="Tín dụng đã sử dụng" value={formatVNDCompact(stats.totalCreditsRedeemed)} icon={<CreditCardIcon className="w-5 h-5" data-id="element-772" />} data-id="element-771" />
        <StatCard label="Nợ tín dụng chưa dùng" value={formatVNDCompact(stats.unredeemedLiability)} icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-774" />} data-id="element-773" />
        <StatCard label="Tỷ lệ mua lại" value={`${stats.repeatPurchaseRate}%`} icon={<RepeatIcon className="w-5 h-5" data-id="element-776" />} trend={{
        value: 3.1,
        isPositive: true
      }} data-id="element-775" />
        <StatCard label="Giỏ hàng trung bình" value={formatVNDCompact(stats.averageBasketSize)} icon={<ShoppingBagIcon className="w-5 h-5" data-id="element-778" />} trend={{
        value: 1.8,
        isPositive: true
      }} data-id="element-777" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-779">
        <Card title="Tăng Trưởng Thành Viên" data-id="element-780">
          <div className="h-64" data-id="element-781">
            <ResponsiveContainer width="100%" height="100%" data-id="element-782">
              <LineChart data={MOCK_MEMBER_GROWTH} data-id="element-783">
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" data-id="element-784" />
                <XAxis dataKey="label" tick={{
                fill: '#666666',
                fontSize: 12
              }} axisLine={{
                stroke: '#2A2A2A'
              }} data-id="element-785" />
                <YAxis tick={{
                fill: '#666666',
                fontSize: 12
              }} axisLine={{
                stroke: '#2A2A2A'
              }} data-id="element-786" />
                <Tooltip contentStyle={{
                backgroundColor: '#1A1A1A',
                border: '1px solid #2A2A2A',
                borderRadius: 8,
                color: '#fff'
              }} labelStyle={{
                color: '#B0B0B0'
              }} data-id="element-787" />
                <Line type="monotone" dataKey="value" stroke="#C8A84E" strokeWidth={2} dot={{
                fill: '#C8A84E',
                r: 4
              }} name="Thành viên" data-id="element-788" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Xu Hướng Nạp Tiền" data-id="element-789">
          <div className="h-64" data-id="element-790">
            <ResponsiveContainer width="100%" height="100%" data-id="element-791">
              <BarChart data={MOCK_DEPOSIT_TRENDS} data-id="element-792">
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" data-id="element-793" />
                <XAxis dataKey="label" tick={{
                fill: '#666666',
                fontSize: 12
              }} axisLine={{
                stroke: '#2A2A2A'
              }} data-id="element-794" />
                <YAxis tick={{
                fill: '#666666',
                fontSize: 12
              }} axisLine={{
                stroke: '#2A2A2A'
              }} tickFormatter={v => formatVNDCompact(v)} data-id="element-795" />
                <Tooltip contentStyle={{
                backgroundColor: '#1A1A1A',
                border: '1px solid #2A2A2A',
                borderRadius: 8,
                color: '#fff'
              }} formatter={(value: number) => [formatVND(value), 'Nạp tiền']} labelStyle={{
                color: '#B0B0B0'
              }} data-id="element-796" />
                <Bar dataKey="value" fill="#C8A84E" radius={[4, 4, 0, 0]} name="Nạp tiền" data-id="element-797" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-798">
        {/* Tier Distribution */}
        <Card title="Phân Bố Hạng" data-id="element-799">
          <div className="h-48" data-id="element-800">
            <ResponsiveContainer width="100%" height="100%" data-id="element-801">
              <PieChart data-id="element-802">
                <Pie data={MOCK_TIER_DISTRIBUTION} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" nameKey="label" data-id="element-803">
                  {MOCK_TIER_DISTRIBUTION.map((_, index) => <Cell key={index} fill={TIER_COLORS[index]} data-id="element-804" />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#1A1A1A',
                border: '1px solid #2A2A2A',
                borderRadius: 8,
                color: '#fff'
              }} data-id="element-805" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 justify-center mt-2" data-id="element-806">
            {MOCK_TIER_DISTRIBUTION.map((item, i) => <div key={i} className="flex items-center gap-1.5" data-id="element-807">
                <div className="w-2.5 h-2.5 rounded-full" style={{
              backgroundColor: TIER_COLORS[i]
            }} data-id="element-808" />
                <span className="text-xs text-[#B0B0B0]" data-id="element-809">
                  {item.label}: {item.value}
                </span>
              </div>)}
          </div>
        </Card>

        {/* Top Customers */}
        <Card title="Top Khách Hàng" className="lg:col-span-2" data-id="element-810">
          <div className="space-y-3" data-id="element-811">
            {topCustomers.map((c, i) => <div key={c.id} className="flex items-center gap-3" data-id="element-812">
                <span className="text-sm font-bold text-[#C8A84E] w-6" data-id="element-813">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0" data-id="element-814">
                  <p className="text-sm font-medium text-white truncate" data-id="element-815">
                    {c.name}
                  </p>
                  <p className="text-xs text-[#666666]" data-id="element-816">{c.phone}</p>
                </div>
                <Badge variant="tier" tier={c.tier} size="sm" data-id="element-817">
                  {c.tier.toUpperCase()}
                </Badge>
                <span className="text-sm font-semibold text-white" data-id="element-818">
                  {formatVNDCompact(c.totalSpent)}
                </span>
              </div>)}
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card title="Hoạt Động Gần Đây" data-id="element-819">
        <div className="space-y-3" data-id="element-820">
          {recentLogs.map(log => <div key={log.id} className="flex items-start gap-3 py-2 border-b border-[#2A2A2A]/50 last:border-0" data-id="element-821">
              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${log.isFlagged ? 'bg-[#EF4444]' : 'bg-[#C8A84E]'}`} data-id="element-822" />
              <div className="flex-1 min-w-0" data-id="element-823">
                <p className="text-sm text-white" data-id="element-824">{log.action}</p>
                <p className="text-xs text-[#666666]" data-id="element-825">
                  {log.staffName} · {formatRelativeTime(log.createdAt)}
                </p>
              </div>
              {log.isFlagged && <Badge variant="error" size="sm" data-id="element-826">
                  Cảnh báo
                </Badge>}
            </div>)}
        </div>
      </Card>
    </div>;
}